import React from "react";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { BotNote } from "features/adminUsers/adminUsers";

/** Only http(s) is rendered as a link: anything else would run whatever
 *  scheme it names in the operator's own session. */
function isWebAddress(url: string | undefined): boolean {
  return !!url && (url.startsWith("https://") || url.startsWith("http://"));
}

/** "Updated by kiril.gogol on 10/19/2026" - a note is somebody's word, and the
 *  operator reading it is owed whose and how old. A seeded note has no editor. */
export function noteByline(note: BotNote): string {
  const when = note.updated_at
    ? new Date(note.updated_at).toLocaleDateString()
    : "";
  const who = note.updated_by || "";
  if (who && when) return `Updated by ${who} on ${when}`;
  if (who) return `Updated by ${who}`;
  return when ? `Updated on ${when}` : "";
}

export function hasNoteContent(
  note: BotNote | null | undefined,
): note is BotNote {
  return !!note && (!!note.text || (note.links ?? []).length > 0);
}

/**
 * What a DPM needs to know before running this vendor's bots, or this bot
 * (CAL-6162).
 *
 * A note is prose plus the pages it points at. A Confluence link is kept by
 * title rather than by URL - that is all the page's storage format holds -
 * so a link with no URL is shown as the title to search for.
 */
export default function BotNoteView({
  note,
  label,
  dense = false,
  inline = false,
  onEdit,
}: {
  note: BotNote | null | undefined;
  /** Whose note this is: "Paylocity" or "download_bots.paystubs". */
  label?: string;
  dense?: boolean;
  /** One flowing line - label, text, byline - for a section header. */
  inline?: boolean;
  /** Given only to whoever may change the note. */
  onEdit?: () => void;
}) {
  if (!hasNoteContent(note)) return null;
  if (inline) {
    return (
      <Box
        sx={(theme) => ({
          flex: "1 1 auto",
          width: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          py: 0.5,
          pl: 1.25,
          pr: 0.5,
          border: 1,
          borderColor: alpha(theme.palette.primary.main, 0.16),
          borderRadius: "6px",
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        })}
      >
        <InfoOutlinedIcon sx={{ flexShrink: 0, mt: "5px", fontSize: 18, color: "text.disabled" }} />
        <Box sx={{ flex: "1 1 auto", minWidth: 0, py: 0.5, fontSize: 14, lineHeight: "20px", overflowWrap: "anywhere" }}>
          {label ? (
            <Box component="span" sx={{ mr: 1, fontSize: 12, fontWeight: 700, color: "text.secondary" }}>
              {label}
            </Box>
          ) : null}
          {note.text ? (
            <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
              {note.text}
            </Box>
          ) : null}
          {(note.links || []).map((link, index) =>
            isWebAddress(link.url) ? (
              <Link
                key={`${link.url}-${index}`}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                sx={{ ml: 1, fontSize: 12 }}
              >
                {link.label || link.url}
              </Link>
            ) : (
              <Chip
                key={`${link.label}-${index}`}
                size="small"
                variant="outlined"
                label={link.label || link.url}
                title="A Confluence page - search for it by this title"
                sx={{ ml: 1, height: 20, fontSize: "0.68rem" }}
              />
            ),
          )}
          {noteByline(note) ? (
            <Box component="span" sx={{ ml: 1, fontSize: 12, color: "text.disabled", whiteSpace: "nowrap" }}>
              {noteByline(note)}
            </Box>
          ) : null}
        </Box>
        {onEdit ? (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon fontSize="small" />}
            onClick={onEdit}
            aria-label={label ? `Edit ${label} note` : "Edit note"}
            sx={{ flexShrink: 0, minWidth: 0, height: 28, mt: "2px" }}
          >
            Edit
          </Button>
        ) : null}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        p: dense ? 1 : 1.25,
        borderRadius: 1,
        bgcolor: "action.hover",
        border: 1,
        borderColor: "divider",
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: "text.disabled", mt: "2px" }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {label ? (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 700, display: "block" }}
            >
              {label}
            </Typography>
          ) : null}
          {note.text ? (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {note.text}
            </Typography>
          ) : null}
          {(note.links || []).length ? (
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {note.links.map((link, index) =>
                isWebAddress(link.url) ? (
                  <Link
                    key={`${link.url}-${index}`}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    variant="caption"
                  >
                    {link.label || link.url}
                  </Link>
                ) : (
                  <Chip
                    key={`${link.label}-${index}`}
                    size="small"
                    variant="outlined"
                    label={link.label || link.url}
                    title="A Confluence page - search for it by this title"
                    sx={{ height: 20, fontSize: "0.68rem" }}
                  />
                ),
              )}
            </Stack>
          ) : null}
          {noteByline(note) ? (
            <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 0.25 }}>
              {noteByline(note)}
            </Typography>
          ) : null}
        </Box>
        {onEdit ? (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon fontSize="small" />}
            onClick={onEdit}
            sx={{ flexShrink: 0, minWidth: 0, py: 0 }}
          >
            Edit
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}
